"use client"

import clsx from "clsx"
import { useRef, useState } from "react"
import { addItemToCart } from "src/checkout/hooks/useCart"
import { useStickyHeaderState } from "src/checkout/layout/stickyHeader"
import { npPagePxClassName } from "src/components/np-design/components/constants"
import { NpButton } from "src/components/np-design/components/NpButton"
import { useIsClient } from "src/hooks/useIsClient"
import { useWindowEvent } from "src/hooks/useWindowEvent"
import { formatPriceShort } from "src/utilities/price"
import { useProductContext } from "./context"

export const ProductStickyMobileHeader = () => {
  const product = useProductContext()
  const isClient = useIsClient()
  const ref = useRef<HTMLDivElement>(null)
  const [scrolledPastRef, setScrolledPastRef] = useState(false)
  const stickyHeaderState = useStickyHeaderState()
  const [adding, setAdding] = useState(false)

  useWindowEvent("scroll", () => {
    if (!ref.current) {
      return
    }

    let el: HTMLElement = ref.current
    let refTop = el.offsetTop - 64 // 64 is the height of the mobile header

    while (el.offsetParent) {
      el = el.offsetParent as HTMLElement
      refTop += el.offsetTop
    }

    const scrollY = window.scrollY
    const scrolledPastRef = scrollY > refTop

    setScrolledPastRef(scrolledPastRef)
  })

  if (!isClient || !product.name || !product.resolvedPrice || !product.resolvedAvailable) {
    return null
  }

  const variationName = product.variation?.name
  const stickyHeaderVisible = stickyHeaderState.visible
  const stickyHeaderHeight = stickyHeaderState.header?.clientHeight || 0
  const top = stickyHeaderVisible ? stickyHeaderHeight : 0

  const stickyHeader = (
    <div
      className={clsx(
        npPagePxClassName,
        "z-sticky-mobile-product-header fixed top-0 right-0 left-0 flex h-16 w-full items-center justify-between gap-4 bg-white/80 shadow backdrop-blur-lg transition-[opacity,transform] duration-300 lg:hidden",
        scrolledPastRef ? "opacity-100" : "opacity-0",
      )}
      style={{ transform: `translateY(${top}px)` }}
    >
      <div className="text-sm leading-snug sm:text-base">
        <div className="flex flex-1 items-start gap-1">
          <span className="font-product">{product.name}</span>-
          <div>
            <span className="font-product">{formatPriceShort(product.resolvedPrice)}</span>
            {product.resolvedRegularPrice && (
              <span className="text-xs leading-none line-through opacity-50">
                {formatPriceShort(product.resolvedRegularPrice)}
              </span>
            )}
          </div>
        </div>

        {variationName && <div className="flex items-center gap-2 whitespace-nowrap">{variationName}</div>}
      </div>

      <NpButton
        size="md"
        iconNameLeft="shopping_cart_add"
        title="In den Warenkorb"
        loading={adding}
        onClick={async () => {
          setAdding(true)
          await addItemToCart(product.resolvedId)
          setAdding(false)
        }}
      />
    </div>
  )

  return (
    <>
      <div ref={ref} className="absolute bottom-0 lg:hidden" />

      {stickyHeader}
    </>
  )
}
